import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { domainKeys, parseDomain, type Domain } from './entity';

/**
 * Domain reads. Same bulletproof shape as the rest of the cluster:
 * a plain async fetcher, a `getXQueryOptions` factory, and a thin hook
 * wrapping `useSuspenseQuery`.
 *
 * The list endpoint sideloads each domain into its own detail slot so
 * follow-up by-id reads (`useDomain({ id })`, when introduced) resolve
 * from cache without an extra GET.
 */

const STALE_TIME = 60_000;

/**
 * Resolves to the URL's workspace id when it's a positive integer; otherwise
 * null. **Reactive** — wraps the React Router `useLocation`-backed hook so
 * persistent components like `delivery-mode-banner` re-render on workspace
 * switch. Don't replace with the non-hook `getWorkspaceIdFromCurrentUrl()`
 * from `@/utils/workspace-id`; that reads `window.location.href` directly
 * and won't trigger re-renders, so the banner would keep rendering the
 * previous workspace's domains until something else forced a re-render.
 */
function useValidWorkspaceId(): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

export interface DomainsList {
  domains: readonly Domain[];
}

/** @public */
export async function getDomains(workspaceId: number, qc: QueryClient): Promise<DomainsList> {
  const data = await hydraUiApi.domains.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (data.domains ?? []).map(parseDomain);
  for (const d of parsed) {
    qc.setQueryData(domainKeys.detail(workspaceId, d.id), d);
  }
  return { domains: parsed };
}

const getDomainsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    // Explicit `undefined` — `TListParams = void` for the domain entity,
    // so this is the no-filters list slot.
    queryKey: domainKeys.list(workspaceId, undefined),
    queryFn: () => getDomains(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseDomainsOptions {
  queryConfig?: QueryConfig<typeof getDomainsQueryOptions>;
}

/** @public */
export const useDomains = ({ queryConfig }: UseDomainsOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getDomainsQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};

/**
 * Non-suspending domains read for callers that mount before the URL
 * settles (workspace-less layouts, root-of-app banners). Returns
 * `data: undefined` while no workspace id is in scope or the fetch is
 * pending; once the fetch lands, `data: { domains: [...] }` (which
 * may itself be empty).
 *
 * Always access via `result.data?.domains` (or destructure with a
 * default) — distinguishing the "loading / no workspace" state from
 * the "loaded but empty" state matters whenever a UX cares about the
 * difference (e.g. banner copy that should hide during loading).
 *
 *   const { data } = useDomainsOptional();
 *   const verifiedDomains = data?.domains.filter((d) => d.verified === true) ?? null;
 *
 *   // Three states the consumer must distinguish:
 *   //   data === undefined        → loading or no workspace yet
 *   //   data?.domains.length === 0 → loaded, workspace has no domains
 *   //   data?.domains.length > 0   → loaded, has domains
 *   if (verifiedDomains == null) return null;          // hide while loading
 *   if (verifiedDomains.length === 0) return <Empty/>; // explicit empty state
 *   return <DomainsList domains={verifiedDomains} />;
 */
interface UseDomainsOptionalOptions<TSelected> {
  /**
   * Memoized projection over the loaded `DomainsList`. Returning a
   * structurally-shared subtree (TanStack Query's `select` does the
   * shallow comparison) keeps the result reference stable across cache
   * updates that don't change the projected value — avoids the
   * caller-side `useMemo` dance for filtered views.
   */
  select?: (data: DomainsList) => TSelected;
}

/** @public */
export function useDomainsOptional(): ReturnType<typeof useQuery<DomainsList>>;
export function useDomainsOptional<TSelected>(
  options: UseDomainsOptionalOptions<TSelected>,
): ReturnType<typeof useQuery<DomainsList, Error, TSelected>>;
export function useDomainsOptional<TSelected = DomainsList>(options?: UseDomainsOptionalOptions<TSelected>) {
  const validWorkspaceId = useValidWorkspaceId();
  const qc = useQueryClient();
  return useQuery({
    // When the workspace id isn't in scope yet, route the query to a
    // sentinel key with `skipToken` for the queryFn — TanStack Query
    // skips the fetch and never registers a `domainKeys.list(0)` slot
    // in the real workspace namespace.
    //
    // The sentinel key sits outside the `['workspace', wsId, ...]`
    // namespace so it survives `clearWorkspaceQueries`, but `skipToken`
    // guarantees no data ever lands here — there is nothing to evict.
    // Don't `setQueryData` against this key; treat it as write-frozen.
    //
    // `skipToken` is load-bearing here, NOT just a stylistic substitute
    // for `enabled: false`. The render where `validWorkspaceId` is null
    // captures a stale `qc` reference; once the workspace resolves, we
    // re-render and recompute both the queryFn closure and the queryKey,
    // so the new fetch sees the current `qc`. Don't drop `skipToken`
    // without also adding `enabled: validWorkspaceId != null`.
    queryKey: validWorkspaceId != null ? domainKeys.list(validWorkspaceId, undefined) : ['domain', 'no-workspace'],
    queryFn: validWorkspaceId != null ? () => getDomains(validWorkspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
    select: options?.select,
  });
}
